// Why a Marketing screen is empty, said out loud.
//
// The Marketing screens used to fetch with `.catch(() => null)`, so a feature
// still in Meta review (403 FEATURE_COMING_SOON), a plan without Marketing
// (403 MODULE_NOT_IN_PLAN), a page or ad account that was never connected
// (400 "integrationId is required" / "adAccountId required") and a real outage
// all rendered as the same zeros and dashes. These helpers keep the reason:
//
//   const res = await fetchApi("/api/insights/overview");
//   if (!res.ok) return <AccessNotice reason={reasonOf(res)} onRetry={load} />;
//
// and `useMarketingAccess()` answers up front what the clinic can use, so a
// screen can skip calls that are bound to fail and show the right card instead.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Clock, Link2, Lock, RefreshCw } from "lucide-react";
import config from "../../../config";
import useFeatureFlags from "../../../hooks/useFeatureFlags";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/**
 * GET (or any method) that never throws and never hides the reason.
 * Resolves to { ok, status, data, code, message } where `data` is the JSON body.
 */
export async function fetchApi(path, { method = "get", params, body, headers } = {}) {
  try {
    const r = await axios({ method, url: `${config.API_BASE_URL}${path}`, params, data: body, headers: { ...auth(), ...headers } });
    const data = r.data;
    if (data && data.success === false) return { ok: false, status: r.status, data, code: data.code || data.error || null, message: data.message || null };
    return { ok: true, status: r.status, data, code: null, message: null };
  } catch (e) {
    const status = e.response?.status ?? 0;
    const data = e.response?.data ?? null;
    // The plan gate sends its reason as `error`, not `code`.
    return { ok: false, status, data, code: data?.code || data?.error || null, message: data?.message || (status ? null : "Can't reach Olli right now. Check your connection.") };
  }
}

/** Turn a failed response into one of the notice kinds below. */
export function reasonOf(res) {
  if (!res || res.ok) return null;
  const msg = String(res.message || "").toLowerCase();
  if (res.code === "FEATURE_COMING_SOON") return { kind: "soon", feature: res.data?.feature };
  if (res.code === "PERMISSION_DENIED") return { kind: "noAccess", message: res.message, needs: res.data?.needs };
  if (res.code === "MODULE_NOT_IN_PLAN" || res.code === "PLAN_LIMIT_REACHED") return { kind: "upgrade" };
  if (msg.includes("adaccountid") || msg.includes("ad account")) return { kind: "adAccount" };
  if (msg.includes("integrationid") || msg.includes("integration not found") || (res.status === 404 && msg.includes("integration"))) return { kind: "connect" };
  if (res.status === 401) return { kind: "error", message: "Your session ended. Sign in again." };
  return { kind: "error", message: res.message || "Something went wrong loading this. Try again." };
}

// What each Meta-review flag unlocks, in the clinic's words.
export const FEATURE_LABEL = {
  meta_page_performance: "Page performance",
  meta_ad_insights: "Ad results",
  meta_page_compose: "Facebook posting",
  meta_page_comments: "Facebook comments",
  meta_instagram_publish: "Instagram posting",
  meta_instagram_insights: "Instagram insights",
  meta_instagram_comments: "Instagram comments",
  meta_messenger_dm: "Messenger replies",
};

/**
 * What this clinic can use in Marketing.
 * { loading, isEnabled(flag), page, instagram, adAccount, integrationsReason, reload }
 *  - page:      the connected Facebook page integration, or null
 *  - instagram: the integration that carries an Instagram business id, or null
 *  - adAccount: { id } when an ad account is configured, else null
 */
// The last /api/integrations failure, if any. A staff member without Settings
// access is refused that lookup; every "Connect your page" card then says
// "No access" instead, because the page may well be connected.
let lastIntegrationsReason = null;

export function useMarketingAccess() {
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const [state, setState] = useState({ loading: true, page: null, instagram: null, adAccount: null, integrations: [], integrationsReason: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [integrations, ads] = await Promise.all([fetchApi("/api/integrations"), fetchApi("/api/integrations/ads/ads-status")]);
    const list = integrations.ok ? integrations.data?.data || [] : [];
    const meta = list.filter((i) => ["meta_leads", "meta_facebook", "meta_instagram"].includes(i.integration_type) && i.is_connected && i.page_id);
    const status = ads.ok ? ads.data?.data || ads.data || {} : {};
    lastIntegrationsReason = reasonOf(integrations);
    setState({
      loading: false,
      // A refused or failed lookup is not "not connected": screens show this instead of a Connect card.
      integrationsReason: reasonOf(integrations),
      integrations: list,
      page: meta[0] || null,
      instagram: meta.find((i) => i.instagram_business_id) || null,
      adAccount: status.configured && status.ad_account_id ? { id: status.ad_account_id } : null,
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  return { ...state, loading: state.loading || flagsLoading, isEnabled, reload: load };
}

const NOTICE = {
  soon: {
    Icon: Clock,
    tone: "bg-sky-soft text-ink",
    title: (r) => {
      const label = FEATURE_LABEL[r.feature] || r.label || "This";
      return `${label} ${/s$/.test(label) ? "are" : "is"} coming soon`; // "Ad results are", "Page performance is"
    },
    body: () => "Meta is still reviewing this permission for Olli. It turns on here as soon as it is approved.",
  },
  connect: {
    Icon: Link2,
    tone: "bg-sun-soft text-ink",
    title: (r) => r.title || "Connect your Facebook page",
    body: (r) => r.body || "Connect the clinic's Facebook page (and the Instagram account linked to it) to see this.",
    action: { label: "Connect Meta", to: "/settings/integrations" },
  },
  instagram: {
    Icon: Link2,
    tone: "bg-sun-soft text-ink",
    title: () => "Connect your Instagram account",
    body: () => "Link the clinic's Instagram business account to its Facebook page, then connect Meta in Olli.",
    action: { label: "Connect Meta", to: "/settings/integrations" },
  },
  adAccount: {
    Icon: Link2,
    tone: "bg-sun-soft text-ink",
    title: () => "Connect an ad account",
    body: () => "Lead forms, audiences and pixels live in a Meta ad account. Connect one to manage them here.",
    action: { label: "Connect ad account", to: "/settings/integrations" },
  },
  upgrade: {
    Icon: Lock,
    tone: "bg-mist text-ink",
    title: () => "Marketing isn't in your plan",
    body: () => "Upgrade to a plan with Marketing to use this.",
    action: { label: "See plans", to: "/subscription/plans" },
  },
  noAccess: {
    Icon: Lock,
    tone: "bg-mist text-ink",
    title: (r) => (r.needs === "edit" ? "View only" : "No access"),
    body: (r) => r.message || "You don't have access to this. Ask an admin to give you access.",
  },
  error: {
    Icon: AlertCircle,
    tone: "bg-danger-soft text-danger",
    title: () => "Couldn't load this",
    body: (r) => r.message || "Something went wrong. Try again.",
  },
};

/**
 * The card a screen (or a panel inside one) shows instead of empty data.
 * reason = { kind: soon|connect|instagram|adAccount|upgrade|error, feature?, message?, title?, body? }
 * compact = a smaller version for a panel inside a page.
 */
export function AccessNotice({ reason, onRetry, compact = false, className = "" }) {
  const navigate = useNavigate();
  if (!reason) return null;
  if ((reason.kind === "connect" || reason.kind === "instagram") && lastIntegrationsReason) reason = lastIntegrationsReason;
  const n = NOTICE[reason.kind] || NOTICE.error;
  const { Icon } = n;
  return (
    <div role={reason.kind === "error" ? "alert" : "status"} className={`flex ${compact ? "flex-row items-start gap-3 p-4" : "flex-col items-center gap-3 px-6 py-12 text-center"} rounded-lg bg-paper shadow-soft ${className}`}>
      <span className={`flex ${compact ? "h-9 w-9" : "h-12 w-12"} shrink-0 items-center justify-center rounded-full ${n.tone}`}>
        <Icon size={compact ? 18 : 22} strokeWidth={1.75} />
      </span>
      <div className={compact ? "min-w-0 flex-1" : "flex max-w-md flex-col items-center"}>
        <p className={`${compact ? "text-small" : "text-body"} font-semibold`}>{n.title(reason)}</p>
        <p className={`mt-1 ${compact ? "text-caption" : "text-small"} text-muted`}>{n.body(reason)}</p>
        {(n.action || (reason.kind === "error" && onRetry)) && (
          <div className={`mt-3 flex flex-wrap gap-2 ${compact ? "" : "justify-center"}`}>
            {n.action && (
              <button type="button" onClick={() => navigate(n.action.to)} className="flex h-9 items-center rounded-full bg-ink px-4 text-caption font-semibold text-paper hover:bg-ink/85">
                {n.action.label}
              </button>
            )}
            {reason.kind === "error" && onRetry && (
              <button type="button" onClick={onRetry} className="flex h-9 items-center gap-1.5 rounded-full bg-paper px-4 text-caption font-semibold text-ink ring-1 ring-line hover:bg-mist">
                <RefreshCw size={14} strokeWidth={1.75} />
                Try again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
